import { Component, OnDestroy, OnInit, ViewChild, signal } from '@angular/core';
import { Subject, fromEvent } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AdminConfigService } from '../../services/admin-config.service';
import { ListDataService } from '../../services/list-data.service';
import { ToastNotificationComponent } from '../../toast-notification/toast-notification.component';
import { ListItem } from '../../models/ListItem';
import { LocationListItem } from '../../models/LocationListItem';
import { LocationSearchParameters } from '../../models/LocationSearchParameters';
import { AcsElevatorLocation, AcsCtrmElevator } from '../models/acs.models';
import { ACS_TOAST_SUCCESS_DURATION, ACS_TOAST_ERROR_DURATION } from '../utils/acs.constants';
import { extractErrorMessage, scopedLocsToListItems, executeBulkDelete, formatBulkDeleteToast } from '../utils/acs.utils';
import { AcsDropdownService } from '../utils/acs-dropdown.service';
import { AcsDropdownState } from '../utils/acs-dropdown-state';

@Component({
  selector: 'app-acs-elevator-match',
  templateUrl: './acs-elevator-match.component.html',
  styleUrls: ['../acs-shared.css'],
  standalone: false
})
export class AcsElevatorMatchComponent implements OnInit, OnDestroy {

  private _destroy$: Subject<void> = new Subject<void>();

  @ViewChild('toastNotification') toastNotification!: ToastNotificationComponent;

  elevatorLocations: AcsElevatorLocation[] = [];
  private _allElevatorLocations: AcsElevatorLocation[] = [];
  locList: ListItem[] = [];
  locDropdown!: AcsDropdownState;
  locationFilter: string = '';
  cityFilter: string = '';

  showDialog: boolean = false;
  isEditing: boolean = false;
  formElevatorLocationId: number = 0;
  formLocCode: string = '';
  formElevatorCode: string = '';
  formLocationId: number | null = null;
  formLocationDisplay: string = '';

  locationResults: LocationListItem[] = [];
  locationSearchText = signal('');
  isSearchingLocations: boolean = false;

  ctrmElevators: AcsCtrmElevator[] = [];
  isLoadingCtrm: boolean = false;

  hasSearched: boolean = false;

  selectedIds: Set<number> = new Set();
  showBulkDeleteConfirm: boolean = false;
  isBulkDeleting: boolean = false;

  constructor(
    private _adminConfigService: AdminConfigService,
    private _listDataService: ListDataService,
    public dropdowns: AcsDropdownService
  ) {
    this.locDropdown = new AcsDropdownState('em-loc', this.dropdowns, () => this.locList, '-- All Trade Units --');
  }

  ngOnInit(): void {
    this._adminConfigService.getScopedLocs().pipe(takeUntil(this._destroy$)).subscribe({
      next: (result) => this.locList = scopedLocsToListItems(result, false),
      error: () => this.locList = []
    });
    fromEvent(document, 'click').pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.dropdowns.close();
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  toggleLocDropdown(): void {
    this.locDropdown.toggle();
  }

  selectLocOption(loc: ListItem | null): void {
    this.locDropdown.select(loc);
    this.loadElevatorLocations();
  }

  loadElevatorLocations(): void {
    this.selectedIds = new Set();
    this.hasSearched = true;
    this._adminConfigService.getElevatorLocations(this.locDropdown.selectedValue, '', '')
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (data) => {
          if (!this.locDropdown.selectedValue) {
            const userLocCodes: Set<string> = new Set(this.locList.map(l => l.value));
            this._allElevatorLocations = data.filter(item => userLocCodes.has(item.locCode));
          } else {
            this._allElevatorLocations = data;
          }
          this.applyFilters();
        },
        error: () => {
          this._allElevatorLocations = [];
          this.elevatorLocations = [];
        }
      });
  }

  applyFilters(): void {
    const locTerm: string = this.locationFilter.toLowerCase().trim();
    const cityTerm: string = this.cityFilter.toLowerCase().trim();
    this.elevatorLocations = this._allElevatorLocations.filter(item => {
      const locationDisplay: string = ((item.locationCode ?? '') + ' ' + (item.locationName ?? '')).toLowerCase();
      const matchesLoc: boolean = !locTerm || locationDisplay.includes(locTerm);
      const matchesCity: boolean = !cityTerm || (item.city ?? '').toLowerCase().includes(cityTerm);
      return matchesLoc && matchesCity;
    });
  }

  toggleLocationDropdown(): void {
    this.dropdowns.toggle('em-modal-location');
    if (this.dropdowns.isOpen('em-modal-location')) this.locationSearchText.set('');
  }

  searchLocations(): void {
    const term: string = this.locationSearchText().trim();
    if (term.length < 2) return;
    this.isSearchingLocations = true;
    const params: LocationSearchParameters = new LocationSearchParameters();
    params.searchTerm = term;
    this._listDataService.searchLocations(params)
      .pipe(takeUntil(this._destroy$)).subscribe({
        next: (data) => {
          this.locationResults = data;
          this.isSearchingLocations = false;
        },
        error: () => {
          this.locationResults = [];
          this.isSearchingLocations = false;
        }
      });
  }

  selectLocation(location: LocationListItem): void {
    this.formLocationId = location.locationId;
    this.formLocationDisplay = location.locationCode + ' - ' + location.locationName
      + (location.city ? ', ' + location.city : '');
    this.locationSearchText.set('');
    this.dropdowns.close();
  }

  selectMapping(mapping: AcsElevatorLocation): void {
    this.isEditing = true;
    this.showDialog = true;
    this.locationResults = [];
    this.formElevatorLocationId = mapping.elevatorLocationId;
    this.formLocCode = mapping.locCode || '';
    this.formElevatorCode = mapping.elevatorCode || '';
    this.formLocationId = mapping.locationId;
    this.formLocationDisplay = mapping.locationCode
      ? mapping.locationCode + ' - ' + (mapping.locationName || '')
        + (mapping.city ? ', ' + mapping.city : '')
      : '';
  }

  addMapping(): void {
    this.isEditing = false;
    this.showDialog = true;
    this.formElevatorLocationId = 0;
    this.formLocCode = this.locDropdown.selectedValue;
    this.formElevatorCode = '';
    this.formLocationId = null;
    this.formLocationDisplay = '';
    this.locationResults = [];
    this.locationSearchText.set('');
    this.loadCtrmElevators(this.formLocCode);
  }

  loadCtrmElevators(locCode: string): void {
    this.ctrmElevators = [];
    if (!locCode) return;
    this.isLoadingCtrm = true;
    this._adminConfigService.getCtrmElevators(locCode)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (data) => {
          this.ctrmElevators = data;
          this.isLoadingCtrm = false;
        },
        error: () => {
          this.ctrmElevators = [];
          this.isLoadingCtrm = false;
        }
      });
  }

  closeDialog(): void {
    this.showDialog = false;
  }

  saveMapping(): void {
    if (this.formLocationId === null) return;
    const dto: Partial<AcsElevatorLocation> = {
      elevatorLocationId: this.formElevatorLocationId,
      elevatorCode: this.formElevatorCode,
      locationId: this.formLocationId,
      locCode: this.formLocCode
    };
    this._adminConfigService.saveElevatorLocation(dto).pipe(takeUntil(this._destroy$)).subscribe({
      next: (result) => {
        if (result) {
          this.toastNotification.showToast('Elevator mapping saved', ACS_TOAST_SUCCESS_DURATION, 'success');
          this.closeDialog();
          this.loadElevatorLocations();
        }
      },
      error: (err) => {
        const msg: string = extractErrorMessage(err, 'Failed to save elevator mapping');
        this.toastNotification.showToast(msg, ACS_TOAST_ERROR_DURATION, 'error');
      }
    });
  }

  // --- Bulk Delete ---

  get selectedCount(): number { return this.selectedIds.size; }
  isSelected(id: number): boolean { return this.selectedIds.has(id); }

  toggleSelect(id: number, event: Event): void {
    event.stopPropagation();
    if (this.selectedIds.has(id)) { this.selectedIds.delete(id); }
    else { this.selectedIds.add(id); }
    this.selectedIds = new Set(this.selectedIds);
  }

  get allVisibleSelected(): boolean {
    return this.elevatorLocations.length > 0 && this.elevatorLocations.every(e => this.selectedIds.has(e.elevatorLocationId));
  }

  toggleSelectAll(): void {
    if (this.allVisibleSelected) {
      for (const e of this.elevatorLocations) { this.selectedIds.delete(e.elevatorLocationId); }
    } else {
      for (const e of this.elevatorLocations) { this.selectedIds.add(e.elevatorLocationId); }
    }
    this.selectedIds = new Set(this.selectedIds);
  }

  requestBulkDelete(): void { this.showBulkDeleteConfirm = true; }
  cancelBulkDelete(): void { this.showBulkDeleteConfirm = false; }

  confirmBulkDelete(): void {
    this.isBulkDeleting = true;
    const ids: number[] = [...this.selectedIds];
    executeBulkDelete(ids, (id: number) => this._adminConfigService.deleteElevatorLocation(id))
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (result) => {
          const toast = formatBulkDeleteToast(result, 'elevator mapping');
          this.toastNotification.showToast(toast.message, toast.type === 'success' ? ACS_TOAST_SUCCESS_DURATION : ACS_TOAST_ERROR_DURATION, toast.type);
          this.selectedIds = new Set();
          this.showBulkDeleteConfirm = false;
          this.isBulkDeleting = false;
          this.loadElevatorLocations();
        }
      });
  }

}
